import { makeAutoObservable, runInAction } from "mobx";
import { utils, camelToTitle } from "@intrasync/utils";
import ServiceStore from "stores/ServiceStore";
import { DataGridColumnHeader } from "types/DataGridTypes";

// Type-safe version of groupBy
function groupBy<T extends Record<string, unknown>>(
  array: T[],
  key: string
): Record<string, T[]> {
  return array.reduce((result, item) => {
    const groupKey = String(item[key] ?? "");
    if (!result[groupKey]) result[groupKey] = [];
    result[groupKey].push(item);
    return result;
  }, {} as Record<string, T[]>);
}

// Define the base types for the data grid
type DataGridRow = Record<string, unknown>;
type DataGridValue = string | number | boolean | null | undefined | object;

interface SortState {
  sortBy: string[];
  sortDirection: Record<string, string>;
}

interface CellHover {
  columnIndex?: number;
  rowIndex?: number;
}

// Sort parameter type
interface SortParams {
  sortKey: string;
  sortKeyType: string;
  sortDirection: string;
}

// Interface for the DataGrid reference
interface DataGridRef {
  recomputeGridSize: () => void;
}

// Interface for the root store
interface RootStore {
  [key: string]: unknown;
}

class DataGridStoreNameSpace {
  rootStore: RootStore;
  namespace: string;
  currentStoreName: string = "";
  uniqueKey: string = "id";
  entityValue: string = "entities";
  columnHeaders: DataGridColumnHeader[] = [];
  sortState: SortState = { sortBy: [], sortDirection: {} };
  filterState: Record<string, string> = {};
  editData: Record<string | number, Record<string | number, DataGridValue>> =
    {};
  currentCellHover: CellHover = {
    columnIndex: undefined,
    rowIndex: undefined,
  };
  fullGridEditMode: boolean = false;
  removeColumnEnabled: boolean = false;
  removeItemColumnHeader: DataGridColumnHeader = {
    key: "remove",
    title: "Remove",
    width: 60,
  };
  dataGridRef: DataGridRef | undefined;

  constructor(rootStore: RootStore, namespace: string) {
    makeAutoObservable(this, { rootStore: false });
    this.rootStore = rootStore;
    this.namespace = namespace;
  }

  setUniqueKey = (value: string) => {
    this.uniqueKey = value;
  };

  setCurrentStoreName = (storeName: string) => {
    this.currentStoreName = storeName;
  };
  setDataGridRef = (newDataGridRef: DataGridRef) => {
    this.dataGridRef = newDataGridRef;
  };

  recomputeDataGrid = () => {
    if (this.dataGridRef) {
      this.dataGridRef.recomputeGridSize();
    }
  };

  setFullGridEditMode = () => {
    this.fullGridEditMode = !this.fullGridEditMode;
    if (!this.fullGridEditMode && Object.keys(this.editData).length > 0) {
      this.resetEditData();
    }
  };

  toggleRemoveColumn = () => {
    this.removeColumnEnabled = !this.removeColumnEnabled;
    if (this.dataGridRef) {
      this.recomputeDataGrid();
    }
  };

  setEditData = (key: string | number, data: object) => {
    this.editData[key] = { ...this.editData[key], ...data };
  };

  removeEditData = (key: string | number, dataKey?: string | number) => {
    if (key && !dataKey) {
      console.log("removeEditData key only", key);
      delete this.editData[key];
    }
    if (
      this.editData[key] &&
      dataKey !== undefined &&
      (this.editData[key][dataKey] !== null ||
        this.editData[key][dataKey] !== undefined)
    ) {
      delete this.editData[key][dataKey];
    }
  };

  setDefaultSort = (defaultSort: SortState) => {
    this.sortState = { ...defaultSort };
  };

  setDefaultFilter = (defaultFilter: Record<string, string>) => {
    this.filterState = { ...defaultFilter };
  };

  setColumnHeaders = (newColumnHeaders: DataGridColumnHeader[]) => {
    const headersWereAlreadySet = this.columnHeaders.length > 0;
    this.columnHeaders = newColumnHeaders;
    if (headersWereAlreadySet && this.dataGridRef) {
      this.recomputeDataGrid();
    }
  };

  setEntityValue = (newEntitiesValue: string) => {
    console.log("Setting entity value to", newEntitiesValue);
    this.entityValue = newEntitiesValue;
  };

  setCurrentCellHover = (columnIndex: number, rowIndex: number) => {
    this.currentCellHover.columnIndex = columnIndex;
    this.currentCellHover.rowIndex = rowIndex;
    return this.currentCellHover;
  };

  resetCellHover = () => {
    this.currentCellHover.columnIndex = undefined;
    this.currentCellHover.rowIndex = undefined;
  };

  setFilter = (columnKey: string | number, filterValue: string) => {
    if (filterValue === "" && this.filterState?.[columnKey]) {
      delete this.filterState[columnKey];
      return;
    }
    this.filterState[columnKey] = filterValue;
    return this.filterState[columnKey];
  };

  handleMultiSort = ({
    sortBy,
    sortDirection,
  }: {
    sortBy: string;
    sortDirection: string;
  }) => {
    if (this.sortState.sortBy.includes(sortBy)) {
      runInAction(() => {
        this.sortState.sortDirection[sortBy] = sortDirection;
      });
      return this.sortState;
    }
    // if (this.sortState?.sortBy?.length === 4) {
    //   return console.warn('Needs to implement something');
    // }
    runInAction(() => {
      this.sortState.sortBy.push(sortBy);
      this.sortState.sortDirection[sortBy] = sortDirection;
    });
    return this.sortState;
  };

  removeSortBy = (sortBy: string) => {
    runInAction(() => {
      this.sortState.sortBy = this.sortState.sortBy.filter(
        (sorted) => !sorted.includes(sortBy)
      );
      delete this.sortState.sortDirection[sortBy];
    });
    return this.sortState;
  };

  addColumnHeader = (newHeader: DataGridColumnHeader, index?: number) => {
    if (index === undefined) {
      this.columnHeaders.push(newHeader);
      if (this.dataGridRef) {
        this.recomputeDataGrid();
      }
      return newHeader.key;
    }
    this.columnHeaders.splice(index, 0, newHeader);
    if (this.dataGridRef) {
      this.recomputeDataGrid();
    }
    return newHeader.key;
  };

  removeColumnHeader = (headerKey?: string, index?: number) => {
    if (headerKey) {
      this.columnHeaders = this.columnHeaders.filter(
        (c) => c.key !== headerKey
      );
      if (this.dataGridRef) {
        this.recomputeDataGrid();
      }
      return headerKey;
    }
    if (index !== undefined) {
      this.columnHeaders.splice(index, 1);
      if (this.dataGridRef) {
        this.recomputeDataGrid();
      }
      return headerKey;
    }
    const poppedHeader = this.columnHeaders.pop();
    if (this.dataGridRef) {
      this.recomputeDataGrid();
    }
    return poppedHeader?.key;
  };

  updateColumnHeader = (
    headerKeyOrIndex: number | string,
    newHeader: DataGridColumnHeader
  ) => {
    if (
      typeof headerKeyOrIndex === "string" &&
      isNaN(parseInt(headerKeyOrIndex))
    ) {
      const foundIndex = this.columnHeaders.findIndex(
        (c) => c.key === headerKeyOrIndex
      );
      if (foundIndex === -1) {
        return console.warn("No header found with key", headerKeyOrIndex);
      }
      this.columnHeaders[foundIndex] = {
        ...this.columnHeaders[foundIndex],
        ...newHeader,
      };
      if (this.dataGridRef) {
        this.recomputeDataGrid();
      }
      return newHeader.key;
    } else {
      let headerKey = headerKeyOrIndex;
      if (typeof headerKey !== "number") {
        headerKey = parseInt(headerKey);
        if (isNaN(headerKey))
          return console.warn("Invalid header key", headerKey);
      }
      if (this.columnHeaders[headerKey] || headerKey < 0)
        return console.warn("No header found with index", headerKey);
      this.columnHeaders[headerKey] = {
        ...this.columnHeaders[headerKey],
        ...newHeader,
      };
      if (this.dataGridRef) {
        this.recomputeDataGrid();
      }
      return newHeader.key;
    }
  };

  setDataGrid = ({
    currentStoreName,
    columnHeaders,
    sortState,
    filterState,
  }: {
    currentStoreName: string;
    columnHeaders: DataGridColumnHeader[];
    sortState?: SortState;
    filterState?: Record<string, string>;
  }) => {
    this.setCurrentStoreName(currentStoreName);
    this.setColumnHeaders(columnHeaders);
    if (sortState) {
      this.setDefaultSort(sortState);
    }
    if (filterState) {
      this.setDefaultFilter(filterState);
    }
  };

  resetEditData = () => {
    this.editData = {};
  };

  resetDataGrid = () => {
    this.currentStoreName = "";
    this.uniqueKey = "id";

    this.fullGridEditMode = false;
    this.removeColumnEnabled = false;

    this.columnHeaders = [];

    this.sortState = { sortBy: [], sortDirection: {} };
    this.filterState = {};
    this.currentCellHover = {
      columnIndex: undefined,
      rowIndex: undefined,
    };
    this.setEntityValue("entities");
    this.resetEditData();
    if (this.removeColumnEnabled) {
      this.toggleRemoveColumn();
    }

    if (this.dataGridRef) {
      this.dataGridRef = undefined;
    }
  };
  dataGridSort = (
    dataTypes: SortParams[],
    rows: DataGridRow[]
  ): DataGridRow[] => {
    const sortFunc = (
      a: DataGridRow,
      b: DataGridRow,
      currentSort: SortParams
    ) => {
      const valueA = a[currentSort.sortKey];
      const valueB = b[currentSort.sortKey];

      if (currentSort.sortKeyType === "number") {
        const numA = Number(valueA) || 0;
        const numB = Number(valueB) || 0;

        if (currentSort.sortDirection === "DESC") {
          return numB - numA;
        }
        return numA - numB;
      }

      if (currentSort.sortKeyType === "string") {
        const strA = String(valueA || "");
        const strB = String(valueB || "");

        if (currentSort.sortDirection === "DESC") {
          return strB.localeCompare(strA);
        }
        return strA.localeCompare(strB);
      }

      if (
        currentSort.sortKeyType === "date" ||
        currentSort.sortKeyType === "time"
      ) {
        const strA = String(valueA || "");
        const strB = String(valueB || "");

        if (currentSort.sortDirection === "DESC") {
          return strB.localeCompare(strA);
        }
        return strA.localeCompare(strB);
      }

      // Default case - convert to string and compare
      const strA = String(valueA || "");
      const strB = String(valueB || "");

      if (currentSort.sortDirection === "DESC") {
        return strB.localeCompare(strA);
      }
      return strA.localeCompare(strB);
    };
    const recursiveSort = (
      currentData: DataGridRow[],
      currentSort: SortParams
    ) => {
      return currentData.sort((a, b) => sortFunc(a, b, currentSort));
    };
    const processGroupBy = (
      sortedArray: DataGridRow[],
      currentSort: SortParams
    ): DataGridRow[][] => {
      const sortKey = currentSort.sortKey;

      // Validate that the sort key exists in the data
      if (sortedArray.length === 0) {
        return [sortedArray];
      }

      const firstItem = sortedArray[0];
      if (!(sortKey in firstItem)) {
        console.warn(
          `Sort key "${sortKey}" does not exist in data. Available keys:`,
          Object.keys(firstItem)
        );
        // Return the array as a single group without grouping
        return [sortedArray];
      }

      if (currentSort.sortKeyType === "number") {
        // For numbers, we need to convert them to strings for groupBy, then back
        const numberSorted = sortedArray.map((i) => ({
          ...i,
          [currentSort.sortKey]: `number-${String(i[currentSort.sortKey])}`,
        }));

        const groupedFirstNumberSort = groupBy(numberSorted, sortKey);
        const arrayOfNumberArrays = Object.values(groupedFirstNumberSort).map(
          (currentArray) =>
            currentArray.map((i) => ({
              ...i,
              [currentSort.sortKey]: String(i[currentSort.sortKey]).replace(
                "number-",
                ""
              ),
            }))
        );
        return arrayOfNumberArrays;
      }

      // For other types, directly group
      const groupedFirstSort = groupBy(sortedArray, sortKey);
      return Object.values(groupedFirstSort);
    }; // Enhanced recursive sorting that can handle multiple levels of sorting
    const processRecursiveSort = (
      data: DataGridRow[],
      sortIndex: number
    ): DataGridRow[] => {
      if (sortIndex >= dataTypes.length) {
        return data;
      }

      const currentSort = dataTypes[sortIndex];

      if (sortIndex === 0) {
        // First level sort
        const firstSort = recursiveSort(data, currentSort);
        const grouped = processGroupBy(firstSort, currentSort);

        if (sortIndex === dataTypes.length - 1) {
          // Only one sort criteria
          return grouped.flat(Infinity) as DataGridRow[];
        }

        // Process next level sorting for each group
        return grouped.flatMap((group) =>
          processRecursiveSort(group, sortIndex + 1)
        );
      } else {
        // For subsequent levels, apply sorting to each array
        if (Array.isArray(data[0])) {
          // Handle nested arrays (result of previous groupBy operations)
          return (data as unknown as DataGridRow[][])
            .map((subArray) => processRecursiveSort(subArray, sortIndex))
            .flat() as DataGridRow[];
        } else {
          // Sort this array and group it
          const sorted = recursiveSort(data, currentSort);
          const grouped = processGroupBy(sorted, currentSort);

          if (sortIndex === dataTypes.length - 1) {
            // Last sort criteria
            return grouped.flat(Infinity) as DataGridRow[];
          }

          // Continue to next level
          return grouped.flatMap((group) =>
            processRecursiveSort(group, sortIndex + 1)
          );
        }
      }
    }; // Start the recursive sorting process
    if (dataTypes.length === 0) {
      return rows;
    }

    return processRecursiveSort(rows, 0);
  };
  get currentStore(): Record<string, unknown> {
    return this.rootStore[this.currentStoreName] as Record<string, unknown>;
  }

  get currentStoreEntities(): DataGridRow[] {
    if (!this.currentStore) return [];

    console.log("currentStoreName", this.currentStoreName);

    console.log("currentStore", this.currentStore);
    console.log("entityValue", this.entityValue);

    if (this.entityValue.includes(".")) {
      const [entities, messageId] = this.entityValue.split(".");
      const sanitizedMessageId = messageId.replace("')", "");
      console.log("entities", entities);
      console.log("sanitizedMessageId", sanitizedMessageId);
      const level1 = this.currentStore[entities] as Array<
        Record<string, unknown>
      >;
      console.log("currentStoreEntities1", level1);
      const level2Variable = level1.find(
        (i: Record<string, unknown>) => i.id === sanitizedMessageId
      ) as Record<string, unknown>;
      console.log("level2Variable", level2Variable);
      if (level2Variable && level2Variable.searchResultIds) {
        const searchResultIds = (
          level2Variable.searchResultIds as Array<string | number>
        ).map((id: string | number) => Number(id));
        console.log("searchResultId", searchResultIds);
        const panelStore = this.rootStore.panelStore as ServiceStore;
        const currentPanels = panelStore.entities.filter((i) => {
          const id = i.id as number;
          return searchResultIds.includes(id);
        });

        console.log("currentPanels", currentPanels);
        return currentPanels;

        // return level2Variable.searchResultId;
      }
      // return {
      //   ...level1,
      // };
      // console.log("entityParts", entityParts);
      // const level1 = this.currentStore[entityParts[0]] as string;
      // console.log("currentStoreEntities1", level1);
      // const nestedVariable = level1[entityParts[1]] as string;
      // console.log("nestedVariable", nestedVariable);
      // const level2 = level1[entityParts[1]] as string;
      // console.log("currentStoreEntities2", level2);
      // const entities2 = this.currentStore[level1][level2];
      // console.log("currentStoreEntities2", entities2);
    }

    const entities = this.currentStore[this.entityValue];
    console.log("currentStoreEntities", entities);
    if (!Array.isArray(entities)) return [];

    return entities.slice().map((i: Record<string, unknown>) => {
      return { ...i };
    });
  }

  get currentData() {
    const data = this.currentStoreEntities;
    if (!data || !data.length) return [];
    const rows =
      data
        ?.slice()
        .filter((i: object) => {
          const filterKeys = Object.keys(this.filterState);
          if (filterKeys.length === 0) return true;

          const checkedValues = Object.entries(i)
            .filter(([key]) => {
              return this.filterState[key];
            })
            .every(([key, value]) => {
              const currentFilterValue = this.filterState[key];
              if (currentFilterValue === "*") {
                return !!value;
              }
              if (currentFilterValue === "!") {
                return !value;
              }
              if (
                currentFilterValue[0] === "!" &&
                currentFilterValue.length > 1
              ) {
                return !String(value)
                  .toLowerCase()
                  .includes(currentFilterValue.slice(1).toLowerCase());
              }
              return `${value}`
                .toLowerCase()
                .includes(currentFilterValue.toLowerCase());
            });
          return checkedValues;
        })
        .map((i: object) => {
          const boolKeys = Object.entries(i).reduce<string[]>(
            (acc, currentEntry) => {
              const [key, value] = currentEntry;
              const foundHeaderType = this.currentColumnHeaders.find(
                (header: DataGridColumnHeader) =>
                  header.key === key && header.dataType
              ) as DataGridColumnHeader | undefined;
              if (foundHeaderType) {
                if (foundHeaderType.dataType === "boolean") {
                  acc.push(key);
                  return acc;
                }
              } else {
                if (typeof value === "boolean") {
                  acc.push(key);
                }
              }
              return acc;
            },
            []
          );
          if (boolKeys.length) {
            const returnItem: DataGridRow = { ...i };
            boolKeys.forEach((key) => {
              returnItem[key] = returnItem[key] ? "True" : "False";
            });
            return returnItem;
          }
          return { ...i };
        }) || [];
    if (this.sortState?.sortBy?.length > 0 && rows.length > 0) {
      // Filter out sort keys that don't exist in the data
      const firstItem = rows[0];
      const validSortKeys = this.sortState.sortBy.filter((sortKey) => {
        if (!(sortKey in firstItem)) {
          console.warn(
            `Removing invalid sort key "${sortKey}" from sortState. Available keys:`,
            Object.keys(firstItem)
          );
          return false;
        }
        return true;
      });

      if (validSortKeys.length === 0) {
        console.warn("No valid sort keys found, returning unsorted data");
        return [...rows];
      }

      const dataTypes = validSortKeys.map((sortKey) => {
        let sortKeyTypeOf = "string";
        const foundHeaderType = this.currentColumnHeaders.find(
          (header: DataGridColumnHeader) =>
            header.key === sortKey && header.dataType
        ) as DataGridColumnHeader | undefined;
        if (foundHeaderType) {
          sortKeyTypeOf = foundHeaderType.dataType || "string";
        } else {
          const firstItem = rows[0];

          let sortKeyTypeOf:
            | "string"
            | "number"
            | "bigint"
            | "boolean"
            | "symbol"
            | "undefined"
            | "object"
            | "function"
            | "date"
            | "time" = typeof firstItem[sortKey];
          if (sortKeyTypeOf === "string" && sortKey.includes("Date")) {
            sortKeyTypeOf = "date";
          }
          if (sortKeyTypeOf === "string" && sortKey.includes("Time")) {
            sortKeyTypeOf = "time";
          }
        }
        return {
          sortKey: sortKey,
          sortKeyType: sortKeyTypeOf,
          sortDirection: this.sortState.sortDirection[sortKey],
        };
      });

      // Use the dataGridSort method for recursive sorting
      const sortedRows = this.dataGridSort(dataTypes, rows);
      return sortedRows;
    }
    return [...rows];
  }

  get currentRows() {
    return [
      { header: "headerRow" },
      { filter: "filterRow" },
      ...this.currentData,
    ];
  }

  get currentColumnHeaders() {
    if (this.columnHeaders) {
      if (this.removeColumnEnabled) {
        return [this.removeItemColumnHeader, ...this.columnHeaders];
      }
      return this.columnHeaders;
    }
    if (this.currentStoreEntities.length > 0) {
      const automaticHeaders = Object.keys(this.currentStoreEntities[0])
        .sort((a, b) => (a < b ? -1 : 1))
        .map((headerKey) => {
          return {
            key: headerKey,
            title: camelToTitle(headerKey),
          };
        });
      if (this.removeColumnEnabled) {
        return [this.removeItemColumnHeader, ...automaticHeaders];
      }
      return automaticHeaders;
    }
    if (this.removeColumnEnabled) {
      return [this.removeItemColumnHeader, { key: "id", title: "Id" }];
    }
    return [{ key: "id", title: "Id" }];
  }

  get currentColumnKeys() {
    return this.currentColumnHeaders.map((column) => column.key);
  }
}

export default DataGridStoreNameSpace;
